import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@app/auth-client";
import { Alert, Box, Button, CircularProgress, Typography } from "@app/ui-kit";

/** Completes the Hosted UI redirect and returns the user to where they started. */
export const AuthCallback = () => {
  const { handleCallback } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [silent, setSilent] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    handleCallback()
      // A silent renewal inside a frame returns null: the result went to the opening page, and this frame shows nothing.
      .then((path) => { if (path === null) setSilent(true); else navigate(path, { replace: true }); })
      .catch((err: unknown) => {
        console.error("Sign-in callback failed", err);
        setError("Sign-in could not be completed. Please try again.");
      });
  }, [handleCallback, navigate]);

  if (silent) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 2
      }}
    >
      {error ? (
        <>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={() => navigate("/", { replace: true })}>
            Back to sign in
          </Button>
        </>
      ) : (
        <>
          <CircularProgress color="secondary" size={28} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Completing sign-in…
          </Typography>
        </>
      )}
    </Box>
  );
};
